/**
 * What an Edit Campaign message changed, as rows a person can read.
 *
 * The raw result JSON on an activity card is gated to super admins
 * (canViewRawJson), so this is the only account every other role gets of an
 * edit. Rows come from data the activity feed already carries — nothing here
 * calls the API:
 *
 *   1. Structured batch edits (CLW grid saves, optimizer Apply Change, undo):
 *      `source_file_metadata.config_payload.batch.edits[]` carries the field,
 *      the old and new value and the entity; `json_response.results[]` says
 *      which edit_ids applied or failed; campaign_change_log rows for the
 *      message's changeGroupId say which were undone since.
 *   2. Single edits (the Edit Campaign modal, pacing, scheduling, Frequence
 *      drops): the executed `json_response.payload` — `{operation, campaigns:
 *      [{id, name}]}` and friends — or the deterministic `config_payload` the
 *      modal sent. Nothing records the previous value for these, so `from` is
 *      null and renders as the new value alone. DV360 / TTD / MadHive / Reddit
 *      report the platform call under `execution_result` (top-level `success`
 *      is the reasoning step); GroundTruth saves no payload and yields no rows.
 */

const isObj = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);

// ── Signatures ──────────────────────────────────────────────────────────────
// A batch edit and its campaign_change_log row are matched by content: the
// log has no edit_id. Shared with CampaignActivity's legacy Apply Change
// group detection, which is where these lived before.

const canonical = (value) => {
  if (Array.isArray(value)) return `[${value.map(canonical).sort().join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${key}:${canonical(value[key])}`).join(',')}}`;
  }
  return String(value ?? '').trim().toLowerCase();
};

export const editSignature = (edit) => canonical({
  field: edit?.field,
  operation: edit?.operation,
  value: edit?.value,
  newValue: edit?.newValue ?? edit?.new_value,
  targets: (edit?.targetEntities || edit?.target_entities || []).map(String).sort(),
});

export const changeSignature = (change) => {
  let value;
  let newValue;
  if (change.op === 'set_field' || change.op === 'list_add') value = change.newValue;
  if (change.op === 'list_remove') value = change.oldValue;
  if (change.op === 'list_edit') {
    value = change.oldValue;
    newValue = change.newValue;
  }
  return editSignature({
    field: change.field,
    operation: change.op,
    value,
    newValue,
    targetEntities: change.undoPayload?.targetEntities || [],
  });
};

// ── Labels and values ───────────────────────────────────────────────────────

const ENTITY_LABELS = {
  line_item: 'Line item',
  ad_group: 'Ad group',
  asset_group: 'Asset group',
  ad_set: 'Ad set',
  ad: 'Ad',
  campaign: 'Campaign',
  creative: 'Creative',
  insertion_order: 'Insertion order',
};

export const entityLabel = (type) => (type ? (ENTITY_LABELS[type] || humanizeField(type)) : '');

const ACRONYMS = {
  io: 'IO', kpi: 'KPI', cpa: 'CPA', cpm: 'CPM', cpc: 'CPC', ctr: 'CTR', roas: 'ROAS',
  id: 'ID', ids: 'IDs', url: 'URL', urls: 'URLs', dma: 'DMA', pmp: 'PMP', fb: 'FB', freq: 'Frequency',
};

/** `ioDailyCap` → "IO daily cap", `campaign_total_budget` → "Campaign total budget". */
export function humanizeField(key) {
  if (key === undefined || key === null || key === '') return '';
  const words = String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_\-.]+/)
    .filter(Boolean)
    .map((w) => ACRONYMS[w.toLowerCase()] || w.toLowerCase());
  if (!words.length) return String(key);
  if (!ACRONYMS[words[0].toLowerCase()]) words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
  return words.join(' ');
}

const MAX_LIST_ITEMS = 8;
const itemText = (x) => (isObj(x)
  ? String(x.name ?? x.keyword ?? x.value ?? x.label ?? x.key ?? x.id ?? JSON.stringify(x))
  : String(x));

export function formatChangeValue(v) {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) {
    if (!v.length) return '—';
    const shown = v.slice(0, MAX_LIST_ITEMS).map(itemText).join(', ');
    return v.length > MAX_LIST_ITEMS ? `${shown} +${v.length - MAX_LIST_ITEMS} more` : shown;
  }
  if (typeof v === 'object') {
    const s = JSON.stringify(v);
    return s.length > 120 ? `${s.slice(0, 117)}…` : s;
  }
  return String(v);
}

export const EDIT_STATUS_LABEL = { applied: 'Applied', failed: 'Failed', pending: 'Pending', undone: 'Undone' };
export const EDIT_STATUS_CLASS = { applied: 'completed', failed: 'failed', pending: 'processing', undone: 'stopped' };
export const NO_CHANGES = Object.freeze({ rows: [], description: null });

/** The "Change" cell: `old → new`, or `Added x` / `Removed x`, or just the new value. */
export function changeText(row) {
  const has = (v) => v !== undefined && v !== null && v !== '';
  if (row.op === 'list_add') return `Added ${formatChangeValue(row.to)}`;
  if (row.op === 'list_remove') return `Removed ${formatChangeValue(row.from)}`;
  if (has(row.from)) return `${formatChangeValue(row.from)} → ${formatChangeValue(row.to)}`;
  return formatChangeValue(row.to);
}

// ── Outcomes ────────────────────────────────────────────────────────────────

const toText = (x) => (typeof x === 'string' ? x : (isObj(x) ? (x.error || x.message || JSON.stringify(x)) : String(x)));

const errorText = (x) => {
  if (!isObj(x)) return null;
  if (x.error) return toText(x.error);
  if (Array.isArray(x.errors) && x.errors.length) return toText(x.errors[0]);
  const nested = errorText(x.api_result);
  if (nested) return nested;
  return x.message ? toText(x.message) : null;
};

/** The whole message's outcome, used for edits without their own result entry. */
function groupOutcome(res, messageStatus) {
  if (messageStatus === 'processing' || messageStatus === 'pending') return { status: 'pending', error: null };
  if (isObj(res)) {
    if (res.success === false) return { status: 'failed', error: errorText(res) };
    // DV360 / TTD / MadHive / Reddit single edits: `success` is the reasoning
    // step; the platform call reports under `execution_result`, and an
    // `incomplete` payload is never executed at all.
    if (isObj(res.execution_result) && res.execution_result.success === false) {
      return { status: 'failed', error: errorText(res.execution_result) };
    }
    if (res.incomplete === true) {
      const missing = Array.isArray(res.missing_fields) && res.missing_fields.length
        ? res.missing_fields.join(', ') : 'required fields';
      return { status: 'failed', error: `Not applied — missing ${missing}` };
    }
  }
  if (['failed', 'error', 'timeout', 'stopped'].includes(messageStatus)) return { status: 'failed', error: errorText(res) };
  return { status: 'applied', error: null };
}

function outcomeOf(entry, fallback) {
  if (isObj(entry) && typeof entry.success === 'boolean') {
    return entry.success ? { status: 'applied', error: null } : { status: 'failed', error: errorText(entry) };
  }
  return fallback;
}

// ── Batch edits ─────────────────────────────────────────────────────────────

// Mirrors recordCampaignChanges' reading of the batch contract: set_field
// carries the new value in `value`, list ops carry the item in `value`,
// list_edit carries old in `value` and new in `newValue`.
function valuesOf(edit) {
  const op = edit.operation;
  if (op === 'set_field') return { from: edit.oldValue ?? null, to: edit.value ?? null };
  if (op === 'list_add') return { from: null, to: edit.value ?? null };
  if (op === 'list_remove') return { from: edit.value ?? null, to: null };
  if (op === 'list_edit') return { from: edit.oldValue ?? edit.value ?? null, to: edit.newValue ?? null };
  return { from: edit.oldValue ?? null, to: edit.newValue ?? edit.value ?? null };
}

const targetsOf = (edit) => (Array.isArray(edit.targetEntities) ? edit.targetEntities
  : (Array.isArray(edit.target_entities) ? edit.target_entities : []));

function batchRows(edits, meta, res, changes, messageStatus) {
  const byId = new Map();
  for (const r of (Array.isArray(res?.results) ? res.results : [])) {
    if (isObj(r) && r.edit_id !== undefined && r.edit_id !== null) byId.set(String(r.edit_id), r);
  }
  const fallback = groupOutcome(res, messageStatus);
  const groupId = meta?.changeGroupId;
  const groupRows = groupId ? (changes || []).filter((c) => c?.changeGroupId === groupId) : [];
  const allReverted = groupRows.length > 0 && groupRows.every((c) => c.status === 'reverted');
  const revertedSignatures = new Set(groupRows.filter((c) => c.status === 'reverted').map(changeSignature));

  return edits.map((edit, index) => {
    const editId = edit.editId ?? edit.edit_id;
    const { from, to } = valuesOf(edit);
    const targets = targetsOf(edit);
    const outcome = outcomeOf(editId !== undefined && editId !== null ? byId.get(String(editId)) : null, fallback);
    const undone = outcome.status === 'applied' && (allReverted || revertedSignatures.has(editSignature(edit)));
    return {
      key: editId !== undefined && editId !== null ? String(editId) : `${edit.field || edit.operation || 'edit'}-${index}`,
      entityType: edit.entityType || edit.entity_type || null,
      entityName: edit.entityName || edit.entity_name || (targets.length ? targets.map(String).join(', ') : null),
      field: edit.fieldLabel || edit.field_label || humanizeField(edit.field) || humanizeField(edit.operation) || 'Change',
      op: edit.operation || null,
      from,
      to,
      status: undone ? 'undone' : outcome.status,
      error: outcome.error,
    };
  });
}

// ── Single-edit payloads ────────────────────────────────────────────────────

const ENTITY_TYPE_BY_KEY = {
  campaigns: 'campaign', ad_sets: 'ad_set', adsets: 'ad_set', ads: 'ad',
  adgroups: 'ad_group', ad_groups: 'ad_group', line_items: 'line_item',
  insertion_orders: 'insertion_order', creatives: 'creative',
};
const snake = (k) => String(k).replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
// Keys that say WHICH entity an edit targets rather than what changed.
const TARGET_KEY = /^(id|(campaign|adgroup|ad_group|adset|ad_set|ad|line_item|insertion_order|entity|advertiser|account|customer|profile|processing|tenant|row)_ids?)$/;
// Plumbing, not changes: operation names, flags, and internal storage
// locations. Storage is matched by key AND by value so a renamed key
// (publishers_csv_path) cannot put a gs:// path in front of every role.
const SKIP_KEY = /^(operation|version|entity_type|dry_run|retry_attempt|incomplete|missing_fields|.*(_path|_bucket|_file|gcs_[a-z_]+))$/;
const isStoragePath = (v) => typeof v === 'string' && /^gs:\/\//.test(v);

/**
 * Flatten an executed payload — `{operation, campaigns: [{id, name}]}`,
 * `{operation, campaign_id, add_zips: [...]}`, a modal `{budget: '500'}` —
 * into rows. Entity lists become one row per changed key per entity; target
 * ids become the entity name; the operation alone becomes a row only when the
 * payload carries nothing else (delete_campaign, pause_campaign).
 */
function payloadToRows(payload, outcome) {
  if (!isObj(payload)) return [];
  const rows = [];
  const push = (entityType, entityName, field, to) => rows.push({
    key: String(rows.length), entityType, entityName, field, op: payload.operation || null, from: null, to, ...outcome,
  });
  const targets = [];
  for (const [key, value] of Object.entries(payload)) {
    const k = snake(key);
    if (value === undefined || value === null || SKIP_KEY.test(k) || isStoragePath(value)) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    if (Array.isArray(value) && value.every(isObj)) {
      const type = ENTITY_TYPE_BY_KEY[k] || null;
      value.forEach((item) => {
        const id = item.id ?? item.campaign_id ?? item.adgroup_id ?? item.entity_id ?? null;
        const fields = isObj(item.params) ? item.params : item;
        const before = rows.length;
        for (const [fk, fv] of Object.entries(fields)) {
          const fkey = snake(fk);
          if (fv === undefined || fv === null || TARGET_KEY.test(fkey) || SKIP_KEY.test(fkey) || isStoragePath(fv)) continue;
          push(type || item.entity_type || null, id !== null ? String(id) : null, humanizeField(fk), fv);
        }
        // An entity with nothing but its id is a target of the operation.
        if (rows.length === before && id !== null) targets.push(String(id));
      });
      continue;
    }
    if (TARGET_KEY.test(k)) { targets.push(formatChangeValue(value)); continue; }
    push(null, null, humanizeField(key), value);
  }
  const target = targets.length ? targets.join(', ') : null;
  if (target) rows.forEach((row) => { if (!row.entityName) row.entityName = target; });
  if (!rows.length && payload.operation) push(null, target, humanizeField(payload.operation), null);
  return rows;
}

/**
 * @param {Object} args
 * @param {Object|string|null} args.metadata  the message's source_file_metadata (user message metadata)
 * @param {Object|null} args.result  the message's json_response
 * @param {Array} args.changes  campaign_change_log rows for the campaign (may be empty)
 * @param {string} args.status  the message status
 * @returns {{rows: Array, description: string|null}}
 */
export function editChangeRows({ metadata = null, result = null, changes = [], status = null } = {}) {
  const meta = isObj(metadata) ? metadata : null;
  const res = isObj(result) ? result : null;

  const batchEdits = Array.isArray(meta?.config_payload?.batch?.edits)
    ? meta.config_payload.batch.edits.filter(isObj) : [];
  if (batchEdits.length) return { rows: batchRows(batchEdits, meta, res, changes, status), description: null };

  const outcome = groupOutcome(res, status);
  const groupId = meta?.changeGroupId;
  // A tracked single edit records its request text as a coarse 'edit' row.
  const description = groupId
    ? ((changes || []).find((c) => c?.changeGroupId === groupId && c.op === 'edit' && c.description)?.description || null)
    : null;

  // Per-edit results whose request is not on the message: a free-form prompt edit,
  // where there is no config_payload.batch to read the request from. The result
  // entries themselves carry what was asked for, so the Entity and Change columns
  // come from those. They used to be hardcoded null here, which rendered every
  // prompt-driven edit as "— | Audiences | Added — | Applied" — the row told you a
  // change happened but not what it was or what it hit.
  const resultRows = (Array.isArray(res?.results) ? res.results : []).filter((r) => isObj(r) && (r.field || r.operation));
  if (resultRows.length) {
    return {
      rows: resultRows.map((r, index) => {
        // The executors speak snake_case; valuesOf/targetsOf read the grid's
        // camelCase request shape. Normalize rather than teach them both.
        const asEdit = {
          operation: r.operation,
          value: r.value,
          newValue: r.newValue ?? r.new_value,
          oldValue: r.oldValue ?? r.old_value,
          targetEntities: r.targetEntities ?? r.target_entities,
        };
        const { from, to } = valuesOf(asEdit);
        const targets = targetsOf(asEdit);
        return {
          key: r.edit_id !== undefined && r.edit_id !== null ? String(r.edit_id) : String(index),
          entityType: r.entityType || r.entity_type || null,
          entityName: targets.length ? targets.map(String).join(', ') : null,
          field: humanizeField(r.field) || humanizeField(r.operation),
          op: r.operation || null,
          from,
          to,
          ...outcomeOf(r, outcome),
        };
      }),
      description,
    };
  }

  const config = isObj(meta?.config_payload) && !meta.config_payload.batch ? meta.config_payload : null;
  const rows = payloadToRows(res?.payload, outcome);
  return { rows: rows.length ? rows : payloadToRows(config, outcome), description };
}
